import { useMemo } from "react";
import PageLayout from "../components/PageLayout";
import { GAUGE_BY_ID } from "../constants/gauges";
import usePilotName from "../hooks/usePilotName";
import { usePublicData } from "../hooks/usePublicData";
import { useLens } from "../lens/LensProvider";
import { fetchGaugeSummary, GaugeSummaryEntry } from "../services/gaugesService";
import { formatRelativeTime } from "../utils/formatters";
import { ordinal } from "../utils/ordinal";

const newestFirst = (a: string | null | undefined, b: string | null | undefined) => (b ?? "").localeCompare(a ?? "");

function CommunityCard({ entry }: { entry: GaugeSummaryEntry }) {
  const gauge = GAUGE_BY_ID.get(entry.gauge_id)!;
  const leader = entry.leader!;
  return (
    <article className="hs-card">
      <h2 className="hs-label">{gauge.label}</h2>
      <div className="hs-value">{gauge.format(leader.value)}</div>
      <div className="hs-meta">
        <a href={`#/pilots/${encodeURIComponent(leader.pilot_uid)}`}>{leader.pilot_name || leader.pilot_uid}</a>
        {leader.recorded_at && <span>{formatRelativeTime(leader.recorded_at)}</span>}
      </div>
    </article>
  );
}

function PilotCard({ entry, pilotUid }: { entry: GaugeSummaryEntry; pilotUid: string }) {
  const gauge = GAUGE_BY_ID.get(entry.gauge_id)!;
  const mine = entry.pilot!;
  const holdsRecord = entry.leader?.pilot_uid === pilotUid;
  return (
    <article className="hs-card">
      <h2 className="hs-label">{gauge.label}</h2>
      <div className="hs-value">{gauge.format(mine.value)}</div>
      <div className="hs-meta">
        {mine.rank != null && mine.of != null ? <span className="hs-rank">{ordinal(mine.rank)} of {mine.of}</span> : <span />}
        {mine.recorded_at && <span>{formatRelativeTime(mine.recorded_at)}</span>}
      </div>
      {entry.leader && (
        <div className="hs-leader">
          {holdsRecord
            ? "Holds this record"
            : <>Leader: <a href={`#/pilots/${encodeURIComponent(entry.leader.pilot_uid)}`}>{entry.leader.pilot_name || entry.leader.pilot_uid}</a> · {gauge.format(entry.leader.value)}</>}
        </div>
      )}
    </article>
  );
}

export default function HighScoresPage() {
  const { activeUid, viewUid, setScope } = useLens();
  const name = usePilotName(activeUid);
  const summary = usePublicData(`gauges:${activeUid ?? ""}`, () => fetchGaugeSummary(activeUid ?? undefined));

  const entries = useMemo(() => {
    const known = (summary.data?.gauges ?? []).filter((entry) => GAUGE_BY_ID.has(entry.gauge_id));
    // Everyone lists what has a leader; the pilot lens lists only what the pilot has a value in.
    return activeUid
      ? known.filter((entry) => entry.pilot).sort((a, b) => newestFirst(a.pilot!.recorded_at, b.pilot!.recorded_at))
      : known.filter((entry) => entry.leader).sort((a, b) => newestFirst(a.leader!.recorded_at, b.leader!.recorded_at));
  }, [summary.data, activeUid]);

  const showEveryone = viewUid
    ? <a href="#/high_scores">Show everyone</a>
    : <button type="button" onClick={() => setScope("everyone")}>Show everyone</button>;

  return (
    <PageLayout route="high_scores" hero={{
      photo: "asat", focus: "100% 50%", title: "High Scores",
      body: "The best of the best, from kills in a single sortie to the fastest, highest and farthest.",
    }}>
      <section className="panel panel-list">
        {summary.loading && <p role="status">Loading high scores…</p>}
        {summary.error && <div className="alert" role="alert">Unable to load high scores. <button onClick={summary.refresh}>Retry</button></div>}
        {summary.data && entries.length === 0 && (
          <p className="dashboard-empty-row">
            {activeUid ? <>{name} has no high scores yet. {showEveryone}</> : "No high scores yet."}
          </p>
        )}
        {entries.length > 0 && (
          <div className="hs-grid">
            {entries.map((entry) => activeUid
              ? <PilotCard key={entry.gauge_id} entry={entry} pilotUid={activeUid} />
              : <CommunityCard key={entry.gauge_id} entry={entry} />)}
          </div>
        )}
      </section>
    </PageLayout>
  );
}
